<template>
  <div class="general-access-selector">
    <div class="flex items-center gap-8 flex-1 min-w-0">
      <BittsAvatar
        v-if="accessType !== 'everyone'"
        :is-icon="true"
        shape="square"
        avatar-classes="!bg-neutral-background-weak"
        size="small"
      >
        <template #icon>
          <FontAwesomeIcon
            :icon="['fak', 'lock']"
            class="text-neutral-text-weak"
            style="height: 11px; width: 11px"
          />
        </template>
      </BittsAvatar>
      <BittsAvatar
        v-else
        :org="currentOrg"
        :show-initials="true"
        size="small"
      />
      <BittsSelect
        :model-value="accessType"
        :options="accessOptions"
        :searchable="false"
        :dropdown-match-select-width="false"
        :class="{
          'general-access-selector__access-select--unset':
            accessType === 'placeholder' && showClearOption,
        }"
        class="min-w-0"
        placeholder="Keep current access"
        dropdown-class-name="general-access-selector__dropdown"
        size="medium"
        @update:model-value="
          (val: string) => onAccessTypeChange(val as AccessType)
        "
      >
        <template #option="{ option }">
          <span
            :class="{
              'text-neutral-text-placeholder': option.value === 'placeholder',
            }"
          >
            {{ option.label }}
          </span>
        </template>
      </BittsSelect>
    </div>
    <ReportRolePicker
      :model-value="rolePickerValue"
      :eligible-permissions="eligiblePermissions"
      :show-remove="false"
      :dropdown-match-select-width="false"
      :class="{ invisible: accessType !== 'everyone' }"
      class="w-120 shrink-0"
      @update:model-value="(val) => (modelValue = val as GeneralAccessLevel)"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsAvatar, BittsSelect } from '@crossbeam/bitts';

import { computed } from 'vue';

import useAuth from '@/composables/useAuth';
import { GENERAL_ACCESS_ONLY_INVITED } from '@/constants/reports';
import type {
  GeneralAccessLevel,
  ReportAccessLevel,
} from '@/types/report_team_access';

import ReportRolePicker from './ReportRolePicker.vue';

const { showClearOption = false } = defineProps<{
  showClearOption?: boolean;
}>();

const modelValue = defineModel<GeneralAccessLevel | null>({ default: null });

const { currentOrg } = useAuth();

type AccessType = 'placeholder' | 'restricted' | 'everyone';

const accessType = computed<AccessType>(() => {
  if (modelValue.value === null) return 'placeholder';
  return modelValue.value === GENERAL_ACCESS_ONLY_INVITED
    ? 'restricted'
    : 'everyone';
});

const rolePickerValue = computed<ReportAccessLevel | null>(() =>
  accessType.value === 'everyone'
    ? (modelValue.value as ReportAccessLevel)
    : null,
);

const accessOptions = computed(() => {
  const options: { value: AccessType; label: string; key: string }[] = [];
  if (showClearOption) {
    options.push({
      value: 'placeholder',
      label: 'Keep current access',
      key: 'placeholder',
    });
  }
  options.push(
    { value: 'restricted', label: 'Only people invited', key: 'restricted' },
    {
      value: 'everyone',
      label: `Everyone at ${currentOrg.value?.name ?? 'Your Organization'}`,
      key: 'everyone',
    },
  );
  return options;
});

const eligiblePermissions: ReportAccessLevel[] = [
  'editor',
  'commenter',
  'viewer',
];

function onAccessTypeChange(value: AccessType) {
  if (value === 'placeholder') {
    modelValue.value = null;
    return;
  }
  modelValue.value =
    value === 'everyone' ? 'editor' : GENERAL_ACCESS_ONLY_INVITED;
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.general-access-selector {
  @apply bg-neutral-background-weak rounded-2xl py-16 px-16 flex items-center justify-between gap-8;
}

.general-access-selector__access-select--unset
  :deep(.ant-select-selection-item) {
  @apply text-neutral-text-placeholder;
}

:deep(.ant-select-selection-item) {
  @apply min-w-0;
}

:deep(.ant-select-selection-item span) {
  @apply block truncate;
}
</style>

<style lang="pcss">
@reference "../../css/theme.css";
.general-access-selector__dropdown {
  min-width: 220px;
}
</style>
